import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Description as SummaryIcon,
  People as AccountabilityIcon,
  Timeline as TimelineIcon,
  FactCheck as AuditIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Incident } from '../Types/Incident';
import { getIncidentDetail } from '../Services/IncidentService';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import { appColors } from '../Config/Theme';
import { useSecurity } from '../Contexts/SecurityProvider';

const PLANNED_SECTIONS = [
  { icon: <SummaryIcon />, title: 'Executive Summary', description: 'High-level overview including incident timeline, key outcomes, and resolution status.' },
  { icon: <AccountabilityIcon />, title: 'Accountability Results', description: 'Final headcount results: total accounted, not accounted, not checked, and breakdown by view.' },
  { icon: <TimelineIcon />, title: 'Activity Timeline', description: 'Chronological log of all headcount updates, status changes, and checkpoints.' },
  { icon: <AuditIcon />, title: 'Audit Trail', description: 'Complete audit history showing who made changes, when, and from which device.' },
];

const IncidentReportPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const [incident, setIncident] = useState<Incident | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });
  const { hasAnyRole } = useSecurity();

  const canEditDetails = incident?.capabilities?.canEditDetails === true
    || hasAnyRole(['Admin', 'IncidentCommander']);

  const loadData = useCallback(async () => {
    if (!incidentId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await getIncidentDetail(incidentId);
      setIncident(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incident.');
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert
          severity="error"
          action={<Button size="small" onClick={loadData}>Retry</Button>}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Breadcrumbs ── */}
      <AppBreadcrumbs items={[
        { label: 'Reports', path: '/reports' },
        { label: incident?.incidentName || 'Incident Report' },
      ]} />

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate('/reports')}
            sx={{ color: appColors.text.body }}
          >
            Back to Reports
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Incident Report
        </Typography>
        {canEditDetails && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/incidents/${incidentId}/edit`)}
            sx={{ fontWeight: 600 }}
          >
            Edit Details
          </Button>
        )}
      </Box>

      {/* ── Incident Info Bar ── */}
      {incident && (
        <IncidentInfoBar
          incidentName={incident.incidentName}
          incidentType={incident.incidentType}
          location={incident.location}
          startedAtUtc={incident.startedAtUtc}
          status={incident.status}
        />
      )}

      {/* ── Summary metrics ── */}
      {incident?.currentCounts && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Total People', value: incident.currentCounts.total, color: appColors.primary.main },
            { label: 'Accounted', value: incident.currentCounts.accounted, color: appColors.status.success },
            { label: 'Not Accounted', value: incident.currentCounts.notAccounted, color: appColors.status.error },
            { label: '% Complete', value: `${Math.round(incident.currentCounts.percentComplete)}%`, color: appColors.primary.main },
          ].map((m) => (
            <Box
              key={m.label}
              sx={{
                flex: '1 1 0',
                minWidth: { xs: 'calc(50% - 8px)', sm: 120 },
                bgcolor: appColors.surface.card,
                borderRadius: '12px',
                border: `1px solid ${appColors.border.default}`,
                px: 2,
                py: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 500 }}>
                {m.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: m.color, lineHeight: 1.3 }}>
                {m.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Planned sections ── */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1.5 }}>
        Report Sections
      </Typography>
      <Typography variant="body2" sx={{ color: appColors.text.caption, mb: 2 }}>
        These report sections are planned and will be available in a future release.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
        {PLANNED_SECTIONS.map((section) => (
          <Box
            key={section.title}
            sx={{
              bgcolor: appColors.surface.card,
              borderRadius: '12px',
              border: `1px solid ${appColors.border.default}`,
              px: 2.5,
              py: 2,
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: appColors.primary.light,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: appColors.primary.main,
                flexShrink: 0,
              }}
            >
              {section.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
                  {section.title}
                </Typography>
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: appColors.status.warningBg,
                    color: appColors.status.warning,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: appColors.text.body, lineHeight: 1.5 }}>
                {section.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>


      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IncidentReportPage;
